<template>
    <aside
        v-if="isRendered"
        class="timer-product-recommendation"
        :class="{ 'is-busy': busy }"
        role="region"
        :aria-label="$t('timersproduct.recommendations.banner_label')"
        :aria-busy="busy ? 'true' : 'false'"
        aria-live="polite"
    >
        <SnackBar
            recommendation-mode
            :recommendation-message="cardMessageHtml"
            :primary-button="primaryLabel"
            :secondary-button="$t('timersproduct.recommendations.not_now')"
            @primary="onPrimary"
            @secondary="onDismiss"
        />
    </aside>
</template>

<script>
import { RECOMMENDATION_VARIANT } from '@/constants'
import SnackBar from '@/plugins/snackbar/SnackBar.vue'

const RECOMMENDATION_BANNER_CLASS = 'has-timer-recommendation-banner'
const RECOMMENDATION_OFFSET_VAR = '--timer-recommendation-offset'
/** Evita el parpadeo al recargar timers (AVAILABLE → processTimers). */
const HIDE_FLICKER_MS = 400

/**
 * Escapa texto interpolado en mensajes con v-html (nombres de producto).
 *
 * @param {unknown} value
 * @returns {string}
 */
function escapeHtml(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
}

/**
 * Shell visual de recomendaciones de Andy (BUILD-7486).
 * Reutiliza SnackBar (alertas al crear timers) y $popup.recommendation.
 * BUILD-7490 decide la variante; BUILD-7491 elimina expirados; BUILD-7493
 * precarga el wizard de añadir; BUILD-7492 cablea la combinada.
 */
export default {
    name: 'TimerProductRecommendation',
    components: {
        SnackBar
    },
    props: {
        /**
         * Nombre del producto sugerido (variante añadir, un solo producto).
         */
        productName: {
            type: String,
            default: ''
        },
        /**
         * BUILD-7492: productos a reponer (naranja sin backup).
         * El contador del banner y del modal usa `length`.
         */
        suggestedProducts: {
            type: Array,
            default() {
                return []
            }
        },
        /**
         * BUILD-7491: bloquea clics mientras se eliminan expirados.
         * No altera el overlay global (Wow / Back to start).
         */
        busy: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isDismissed: false,
            isRendered: false,
            pinnedVariant: '',
            pinnedExpiredCount: 0,
            hideDelayId: null
        }
    },
    computed: {
        variant() {
            return this.$store.getters['timerproduct/getActiveRecommendationVariant'] || ''
        },
        displayVariant() {
            return this.variant || this.pinnedVariant
        },
        widgetData() {
            return this.$store.getters['timerproduct/getHomeWidgetData'] || {}
        },
        expiredCount() {
            return Number(this.widgetData.to_delete) || 0
        },
        displayExpiredCount() {
            return this.expiredCount > 0 ? this.expiredCount : this.pinnedExpiredCount
        },
        suggestedCount() {
            if (this.suggestedProducts.length > 0) {
                return this.suggestedProducts.length
            }

            return Number(this.widgetData.close_to_expire) || 0
        },
        isVisible() {
            return Boolean(this.variant) && !this.isDismissed
        },
        isStoreDismissed() {
            return Boolean(this.$store.state.timerproduct.recommendationDismissed)
        },
        isPortrait() {
            return ['portrait'].includes(this.$mq)
        },
        cardMessageHtml() {
            if (this.displayVariant === RECOMMENDATION_VARIANT.DELETE_EXPIRED) {
                return this.$t('timersproduct.recommendations.delete_expired.message', {
                    n: this.displayExpiredCount
                })
            }

            if (this.displayVariant === RECOMMENDATION_VARIANT.COMBINED) {
                return this.$t('timersproduct.recommendations.combined.message', {
                    expired: this.displayExpiredCount,
                    suggested: this.suggestedCount
                })
            }

            if (this.productName) {
                return this.$t('timersproduct.recommendations.add_products.message', {
                    product: escapeHtml(this.productName)
                })
            }

            return this.$t('timersproduct.recommendations.add_products.message_generic')
        },
        modalMessageHtml() {
            return this.$t('timersproduct.recommendations.modal.message', {
                expired: this.displayExpiredCount,
                suggested: this.suggestedCount
            })
        },
        primaryLabel() {
            if (this.displayVariant === RECOMMENDATION_VARIANT.DELETE_EXPIRED) {
                return this.$t('timersproduct.recommendations.delete_expired.action')
            }

            if (this.displayVariant === RECOMMENDATION_VARIANT.COMBINED) {
                const key = this.isPortrait
                    ? 'timersproduct.recommendations.combined.action_short'
                    : 'timersproduct.recommendations.combined.action'
                return this.$t(key)
            }

            return this.$t('timersproduct.recommendations.add_products.action')
        }
    },
    watch: {
        variant(value) {
            if (value) {
                this.isDismissed = false
            }
        },
        isStoreDismissed(dismissed) {
            if (!dismissed) {
                return
            }

            this.isDismissed = true
            this.hideRecommendation()
        },
        isVisible: {
            immediate: true,
            handler(visible) {
                this.syncVisibility(visible)
            }
        }
    },
    beforeDestroy() {
        this.clearHideDelay()
        this.teardownSnackbarStack()
    },
    methods: {
        /**
         * Mantiene el banner montado si el trigger parpadea al recargar timers.
         *
         * @param {boolean} visible
         */
        syncVisibility(visible) {
            if (visible) {
                this.clearHideDelay()
                this.pinCurrentRecommendation()
                this.isRendered = true
                this.$nextTick(() => {
                    this.syncSnackbarStack()
                })
                return
            }

            if (this.isDismissed || this.busy) {
                this.hideRecommendation()
                return
            }

            this.clearHideDelay()
            this.hideDelayId = setTimeout(() => {
                this.hideDelayId = null
                if (!this.isVisible) {
                    this.hideRecommendation()
                }
            }, HIDE_FLICKER_MS)
        },
        pinCurrentRecommendation() {
            this.pinnedVariant = this.variant
            if (this.expiredCount > 0) {
                this.pinnedExpiredCount = this.expiredCount
            }
        },
        hideRecommendation() {
            this.clearHideDelay()
            this.isRendered = false
            this.pinnedVariant = ''
            this.pinnedExpiredCount = 0
            this.teardownSnackbarStack()
        },
        clearHideDelay() {
            if (this.hideDelayId) {
                clearTimeout(this.hideDelayId)
                this.hideDelayId = null
            }
        },
        /**
         * Sube el $snackbar global para que no tape el banner persistente.
         */
        syncSnackbarStack() {
            if (!this.isRendered) {
                this.teardownSnackbarStack()
                return
            }

            const bar = this.$el && this.$el.querySelector ? this.$el.querySelector('.tooltip-container') : null
            if (!bar) {
                this.teardownSnackbarStack()
                return
            }

            document.body.classList.add(RECOMMENDATION_BANNER_CLASS)
            this.setSnackbarOffset(bar)
            this.observeBanner(bar)
        },
        setSnackbarOffset(bar) {
            const height = Math.ceil(bar.getBoundingClientRect().height)
            document.body.style.setProperty(RECOMMENDATION_OFFSET_VAR, `${height + 8}px`)
        },
        observeBanner(bar) {
            if (this.bannerResizeObserver || typeof ResizeObserver === 'undefined') {
                return
            }

            this.bannerResizeObserver = new ResizeObserver(() => {
                if (!this.isRendered) {
                    return
                }

                const current = this.$el && this.$el.querySelector ? this.$el.querySelector('.tooltip-container') : null
                if (current) {
                    this.setSnackbarOffset(current)
                }
            })
            this.bannerResizeObserver.observe(bar)
        },
        teardownSnackbarStack() {
            if (this.bannerResizeObserver) {
                this.bannerResizeObserver.disconnect()
                this.bannerResizeObserver = null
            }

            document.body.classList.remove(RECOMMENDATION_BANNER_CLASS)
            document.body.style.removeProperty(RECOMMENDATION_OFFSET_VAR)
        },
        onDismiss() {
            if (this.busy) {
                return
            }

            this.isDismissed = true
            this.hideRecommendation()
            this.$emit('dismiss')
        },
        onPrimary() {
            if (this.busy) {
                return
            }

            const variant = this.displayVariant
            if (variant === RECOMMENDATION_VARIANT.COMBINED) {
                this.openReviewModal()
                this.$emit('review')
                return
            }

            this.$emit('primary', variant)
        },
        openReviewModal() {
            const self = this
            this.$popup.recommendation({
                message: this.modalMessageHtml,
                callCancel: function () {
                    self.onDismiss()
                },
                callCancel2: function () {
                    self.$emit('remove-only')
                },
                callSave: function () {
                    self.$emit('remove-and-add')
                }
            })
        }
    }
}
</script>

<style lang="scss" scoped>
.timer-product-recommendation.is-busy {
    pointer-events: none;
}
</style>
